import { useState } from "react";
import {
  FileText,
  Copy,
  Download,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Eye,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "../../lib/utils";

export interface Artifact {
  id: string;
  title: string;
  type: "markdown" | "html" | "json" | "text";
  content: string;
  nodeId?: string;
  nodeName?: string;
  timestamp?: string;
}

interface ArtifactPanelProps {
  artifacts: Artifact[];
  onUpdate?: (id: string, content: string) => void;
  onClose?: () => void;
  className?: string;
}

/**
 * Open-canvas style artifact panel for the Agent Studio.
 * Displays documents generated during agent execution (clinical letters,
 * auth forms, patient summaries) with inline editing support.
 */
export function ArtifactPanel({ artifacts, onUpdate, onClose, className }: ArtifactPanelProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState("");

  if (artifacts.length === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground", className)}>
        <FileText className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm font-medium">No artifacts generated</p>
        <p className="text-xs mt-1">Documents will appear here during execution</p>
      </div>
    );
  }

  const active = artifacts[Math.min(activeIndex, artifacts.length - 1)];

  function startEdit() {
    setEditContent(active.content);
    setEditMode(true);
  }

  function saveEdit() {
    if (onUpdate) onUpdate(active.id, editContent);
    setEditMode(false);
  }

  function handleCopy() {
    navigator.clipboard.writeText(active.content);
  }

  function handleDownload() {
    const ext = active.type === "markdown" ? "md" : active.type === "json" ? "json" : "txt";
    const blob = new Blob([active.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.title.replace(/\s+/g, "-").toLowerCase()}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className={cn("flex flex-col h-full bg-background", className)}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold truncate">{active.title}</h3>
          {active.nodeName && (
            <p className="text-[10px] text-muted-foreground truncate">
              Generated by {active.nodeName}
            </p>
          )}
        </div>

        {/* Navigation */}
        {artifacts.length > 1 && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={activeIndex === 0}
              onClick={() => setActiveIndex((i) => i - 1)}
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span>
              {activeIndex + 1}/{artifacts.length}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={activeIndex >= artifacts.length - 1}
              onClick={() => setActiveIndex((i) => i + 1)}
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-0.5">
          {editMode ? (
            <>
              <Button variant="ghost" size="icon-sm" onClick={() => setEditMode(false)} title="Cancel">
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={saveEdit} title="Save" className="text-emerald-600">
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="icon-sm" onClick={startEdit} title="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={handleCopy} title="Copy">
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={handleDownload} title="Download">
                <Download className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {onClose && (
            <Button variant="ghost" size="icon-sm" onClick={onClose} title="Close" className="ml-1">
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {editMode ? (
          <textarea
            className="w-full h-full p-4 text-sm font-mono bg-background text-foreground resize-none outline-none"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            spellCheck={false}
          />
        ) : (
          <div className="p-4">
            {active.type === "markdown" || active.type === "text" ? (
              <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed whitespace-pre-wrap">
                {active.content}
              </div>
            ) : active.type === "json" ? (
              <pre className="text-xs font-mono bg-zinc-50 dark:bg-zinc-900 rounded-lg p-3 overflow-x-auto">
                {JSON.stringify(JSON.parse(active.content), null, 2)}
              </pre>
            ) : (
              <div className="text-sm" dangerouslySetInnerHTML={{ __html: active.content }} />
            )}
          </div>
        )}
      </div>

      {/* Artifact tabs (if > 1) */}
      {artifacts.length > 1 && (
        <div className="shrink-0 border-t border-border px-2 py-1.5 flex gap-1 overflow-x-auto">
          {artifacts.map((a, i) => (
            <button
              key={a.id}
              onClick={() => {
                setActiveIndex(i);
                setEditMode(false);
              }}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium transition-colors shrink-0",
                i === activeIndex
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-accent/50",
              )}
            >
              <FileText className="h-3 w-3" />
              <span className="truncate max-w-[120px]">{a.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
